import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import ReactLoading from "react-loading";
import { getThemeColor } from '../../common/utils';
import { Helmet } from 'react-helmet';
import '../WebViewPage/WebViewPage.css';
import NotFoundPageWithoutBackButton from '../NotFoundPageWithoutBackButton';
import Top1MedalIcon from '../../assets/images/top1-medal-icon.svg';
import Top2MedalIcon from '../../assets/images/top2-medal-icon.svg';
import Top3MedalIcon from '../../assets/images/top3-medal-icon.svg';
import DefaultAvatar from '../../assets/images/default-avatar.png';
import ChevronRight from '../../assets/images/chevron-right.svg';

const WebViewPage = () => {
    const location = useLocation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [status, setStatus] = useState('Đang diễn ra');
    const [imageLoaded, setImageLoaded] = useState(false);
    const [showRules, setShowRules] = useState(false);
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);
    const [joinContestLoading, setJoinContestLoading] = useState(false);
    const [avatarLoadedStates, setAvatarLoadedStates] = useState({});
    const [isJoinContest, setIsJoinContest] = useState(false);
    const [canJoin, setCanJoin] = useState(true);
    const leaderboardLoadedRef = useRef(false); // Ngăn load leaderboard nhiều lần (StrictMode)
    
    // Hàm helper để gọi Flutter (theo chuẩn callbackFlutter ở source kia)
    // Ở đây chỉ cần truyền tên message, ví dụ: 'callbackDisableLoading'
    const callFlutterHandler = (flutterMessage) => {
        try {
            console.log('Calling Flutter handler:', flutterMessage);

            // 1. Flutter InAppWebView (Flutter app)
            if (window.flutter_inappwebview && typeof window.flutter_inappwebview.callHandler === 'function') {
                window.flutter_inappwebview.callHandler(flutterMessage);
                console.log('Flutter InAppWebView handler called successfully');
                return true;
            }

            // 2. WKWebView (iOS)
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers[flutterMessage]) {
                window.webkit.messageHandlers[flutterMessage].postMessage(flutterMessage);
                console.log('WKWebView handler called successfully');
                return true;
            }

            // 3. Android WebView
            if (window.AndroidWebView && typeof window.AndroidWebView.postMessage === 'function') {
                window.AndroidWebView.postMessage(flutterMessage);
                console.log('Android WebView handler called successfully');
                return true;
            }

            console.log('No WebView handler available for Flutter message:', flutterMessage);
            return false;
        } catch (error) {
            console.error('Error calling Flutter handler:', error);
            return false;
        }
    };
    
    // Chuyển mã màu hex sang rgba với alpha
    const hexToRgba = (hex, alpha = 1) => {
        if (!hex) return `rgba(0,0,0,${alpha})`;
        let normalized = hex.trim();
        if (normalized.startsWith('#')) normalized = normalized.slice(1);

        // Hỗ trợ 3, 4, 6, 8 ký tự hex
        if (normalized.length === 3) {
            const r = parseInt(normalized[0] + normalized[0], 16);
            const g = parseInt(normalized[1] + normalized[1], 16);
            const b = parseInt(normalized[2] + normalized[2], 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
        if (normalized.length === 4) {
            const r = parseInt(normalized[0] + normalized[0], 16);
            const g = parseInt(normalized[1] + normalized[1], 16);
            const b = parseInt(normalized[2] + normalized[2], 16);
            // Bỏ qua alpha trong hex, dùng alpha tham số (0.8)
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
        if (normalized.length === 6 || normalized.length === 8) {
            const r = parseInt(normalized.slice(0, 2), 16);
            const g = parseInt(normalized.slice(2, 4), 16);
            const b = parseInt(normalized.slice(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
        return `rgba(0,0,0,${alpha})`;
    };

    // Lấy id từ URL (query parameter hoặc pathname)
    const getIdFromUrl = () => {
        // Lấy từ query string: ?id=123
        const searchParams = new URLSearchParams(location.search);
        const queryId = searchParams.get('id');
        if (queryId) return queryId;
        
        // Lấy từ pathname: /webview/123
        const pathParts = location.pathname.split('/').filter(part => part);
        if (pathParts.length > 1 && pathParts[0] === 'contest') {
            return pathParts[1];
        }
        
        return null;
    };
    
    const urlId = getIdFromUrl();

    // Kiểm tra nếu không có id thì hiển thị NotFoundPage
    if (!urlId) {
        return <NotFoundPageWithoutBackButton />;
    }

    useEffect(() => {
        // Tải dữ liệu cần thiết
        loadData();
    }, []);

    // Gọi API leaderboard khi data đã load xong
    useEffect(() => {
        if (data && status !== 'Sắp diễn ra') {
            loadLeaderboard();
        }
    }, [data, status]);

    // Reset cờ khi đổi contest để cho phép fetch leaderboard lại
    useEffect(() => {
        leaderboardLoadedRef.current = false;
    }, [data?.id]);

    // Trigger load avatar khi leaderboardData thay đổi
    useEffect(() => {
        if (leaderboardData.length > 0) {
            leaderboardData.forEach((item, index) => {
                if (item.avatar) {
                    handleAvatarLoad(index, item.avatar);
                }
            });
        }
    }, [leaderboardData]);

    // Function tính toán thời gian còn lại
    const calculateTimeRemaining = () => {
        if (!data?.timeStartRound1 || !data?.timeEndRound2) return;

        const now = new Date().getTime();
        const startDate = new Date(data.timeStartRound1).getTime();
        const endDate = new Date(data.timeEndRound2).getTime();

        // Xác định trạng thái
        if (now < startDate) {
            setStatus('Sắp diễn ra');
            const timeDiff = startDate - now;
            const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
            setTimeRemaining({ days, hours, minutes, seconds });
        } else if (now >= startDate && now <= endDate) {
            setStatus('Đang diễn ra');
            const timeDiff = endDate - now;
            const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
            setTimeRemaining({ days, hours, minutes, seconds });
        } else {
            setStatus('Đã kết thúc');
            setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        }
    };

    const getDescriptionText = () => {
        const contestName = data?.name || 'RƯƠNG MAY MẮN';
        if (status === 'Sắp diễn ra') {
            return `Cuộc thi "${contestName}" sắp diễn ra mời các ca sĩ cùng tham gia đoạt giải`;
        }
        if (status === 'Đang diễn ra') {
            return `Cuộc thi "${contestName}" đang diễn ra mời các ca sĩ cùng tham gia đoạt giải`;
        }
        return `Cuộc thi "${contestName}" đã kết thúc chúc mừng các ca sĩ đã đoạt giải`;
    };

    // Function để render rank (số hoặc icon medal)
    const renderRank = (index) => {
        const rank = index + 1;
        
        // Nếu cuộc thi đã kết thúc và là top 3, hiển thị icon medal
        if (status === 'Đã kết thúc' && rank <= 3) {
            switch (rank) {
                case 1:
                    return <img src={Top1MedalIcon} alt="Top 1" className="medal-icon" />;
                case 2:
                    return <img src={Top2MedalIcon} alt="Top 2" className="medal-icon" />;
                case 3:
                    return <img src={Top3MedalIcon} alt="Top 3" className="medal-icon" />;
                default:
                    return rank;
            }
        }
        
        // Các trường hợp khác hiển thị số
        return rank;
    };

    // Cập nhật thời gian mỗi giây
    useEffect(() => {
        if (!data) return;

        calculateTimeRemaining();
        const interval = setInterval(calculateTimeRemaining, 1000);

        return () => clearInterval(interval);
    }, [data]);

    const loadData = async () => {
        try {
            setLoading(true);
            setImageLoaded(false);
            
            // Gọi API với id từ URL
            const response = await axios.get(`${global.config.apiDomain}/game/web/web-view/${urlId}`);
            
            if (response.data.status === 'OK' && response.data.data?.webViewCustom) {
                const webViewData = response.data.data.webViewCustom;
                const isJoin = Boolean(response?.data?.data?.isJoinContest);
                // BXH Bài Thu: canJoin=false khi Level ngoài khoảng admin cấu hình → ẩn nút Tham gia.
                setCanJoin(response?.data?.data?.canJoin !== false);
                
                // Nếu có ảnh, preload ảnh trước
                if (webViewData.imgUrl) {
                    const img = new Image();
                    img.onload = () => {
                        // Batch state updates để giảm re-render
                        requestAnimationFrame(() => {
                            setIsJoinContest(isJoin);
                            setData(webViewData);
                            setImageLoaded(true);
                            setLoading(false);
                            // Gọi Flutter handler sau khi API và ảnh load xong
                            callFlutterHandler('callbackDisableLoading');
                        });
                    };
                    img.onerror = () => {
                        // Nếu ảnh lỗi, vẫn hiển thị nhưng không có ảnh
                        requestAnimationFrame(() => {
                            setIsJoinContest(isJoin);
                            setData(webViewData);
                            setImageLoaded(true);
                            setLoading(false);
                            // Gọi Flutter handler sau khi API load xong (ảnh lỗi nhưng data vẫn có)
                            callFlutterHandler('callbackDisableLoading');
                        });
                    };
                    img.src = webViewData.imgUrl;
                } else {
                    // Không có ảnh thì set data luôn
                    setIsJoinContest(isJoin);
                    setData(webViewData);
                    setImageLoaded(true);
                    setLoading(false);
                    // Gọi Flutter handler sau khi API load xong
                    callFlutterHandler('callbackDisableLoading');
                }
            } else {
                // Nếu webViewCustom null hoặc không có data
                setError('Không tìm thấy dữ liệu cuộc thi');
                setLoading(false);
                // Gọi Flutter handler khi không có data
                callFlutterHandler('callbackDisableLoading');
            }
        } catch (err) {
            setError('Không thể tải dữ liệu');
            console.error('Error loading data:', err);
            setLoading(false);
            // Gọi Flutter handler khi API lỗi
            callFlutterHandler('callbackDisableLoading');
        }
    };

    // Function gọi API leaderboard
    const loadLeaderboard = async () => {
        if (!data?.id || !data?.timeStartRound1 || !data?.timeEndRound2) return;
        if (leaderboardLoadedRef.current) return;
        leaderboardLoadedRef.current = true;
        
        try {
            setLeaderboardLoading(true);
            
            // Chuyển đổi thời gian thành Unix timestamp (seconds)
            const timeStartUnix = Math.floor(new Date(data.timeStartRound1).getTime());
            const timeEndUnix = Math.floor(new Date(data.timeEndRound2).getTime());
            
            const response = await axios.get(`${global.config.apiDomain}/game/web/leaderboard`, {
                params: {
                    contestId: data.id,
                    timeStartRound1: timeStartUnix,
                    timeEndRound2: timeEndUnix,
                    leaderboardKind: data?.leaderboardKind || 'TREASURE'
                }
            });
            
            if (response.data.status === 'OK' && response.data.data?.leaderboard) {
                setLeaderboardData(response.data.data.leaderboard);
            }
        } catch (err) {
            console.error('Error loading leaderboard:', err);
        } finally {
            setLeaderboardLoading(false);
        }
    };

    // Function để handle load avatar
    const handleAvatarLoad = (index, avatarUrl) => {
        if (!avatarUrl) return;
        
        const img = new Image();
        img.onload = () => {
            setAvatarLoadedStates(prev => ({
                ...prev,
                [index]: true
            }));
        };
        img.onerror = () => {
            // Nếu ảnh lỗi, giữ nguyên default avatar
            setAvatarLoadedStates(prev => ({
                ...prev,
                [index]: false
            }));
        };
        img.src = avatarUrl;
    };

    // Function gọi API join contest
    const joinContest = async () => {
        if (!data?.id) return;
        
        try {
            setJoinContestLoading(true);
            
            const response = await axios.get(`${global.config.apiDomain}/game/web/join-contest/${data.id}`);
            
            if (response.data.status === 'OK') {
                // Nếu join thành công, redirect đến deep link
                setIsJoinContest(true);
                if (data.deepLinkJoinButton) {
                    window.location.href = data.deepLinkJoinButton;
                }
            } else {
                console.error('Join contest failed:', response.data.message);
                // Level được kiểm tra lại lúc bấm Tham gia → hiển thị đúng lý do backend trả về.
                alert(response.data.message || 'Không thể tham gia cuộc thi. Vui lòng thử lại.');
            }
        } catch (err) {
            console.error('Error joining contest:', err);
            alert('Có lỗi xảy ra khi tham gia cuộc thi. Vui lòng thử lại.');
        } finally {
            setJoinContestLoading(false);
        }
    };

    // Loading mặc định trong lúc gọi API
    if (loading) {
        return (
            <div style={{display: 'flex',  justifyContent:'center', alignItems:'center', height: '100vh'}}>
                <ReactLoading type="bubbles" color={getThemeColor()} height={200} width={100} />
            </div>
        );
    }

    // Nếu API không có data hoặc lỗi, hiển thị NotFound
    if (error) {
        return <NotFoundPageWithoutBackButton />;
    }

    // Màu nền cho ô bắt đầu/kết thúc theo trạng thái
    const inactiveBg = 'rgba(255,255,255,0.4)';
    const startBoxBg = status === 'Đang diễn ra' ? '#FFFFFF' : inactiveBg;
    const endBoxBg = status === 'Đã kết thúc' ? '#FFFFFF' : inactiveBg;
    const contestColor = data?.backgroundCode?.backgroundCodeContest || '#000000';
    const startTextColor = startBoxBg === '#FFFFFF' ? contestColor : '#FFFFFF';
    const endTextColor = endBoxBg === '#FFFFFF' ? contestColor : '#FFFFFF';
    return (
        <>
            <Helmet>
                <title>{data?.name ? `${data.name}` : ''}</title>
                <meta name="description" content={getDescriptionText()} />
                <meta property="og:title" content={data?.name ? `${data.name}` : ''} />
                <meta property="og:description" content={getDescriptionText()} />
                <meta name="twitter:title" content={data?.name ? `${data.name}` : ''} />
            </Helmet>
            <div 
                className="webview-container"
                style={{
                    backgroundColor: data?.backgroundCode?.backgroundCodeContest || '#f5f5f5',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            >
                {/* Modal Thể lệ - Fullscreen */}
                {showRules && data?.ruleImgUrl ? (
                    <div className="rules-modal" onClick={() => setShowRules(false)}>
                        <button className="rules-close-btn" onClick={() => setShowRules(false)}>
                            <i className="fa fa-times"></i>
                        </button>
                        <div className="rules-content" onClick={(e) => e.stopPropagation()}>
                            <img 
                                src={data.ruleImgUrl} 
                                alt="Thể lệ cuộc thi" 
                                className="rules-img"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="webview-content">
                        {/* Background Image */}
                        {data?.imgUrl && (
                            <div className="background-image">
                                <img 
                                    src={data.imgUrl} 
                                    alt="Background"
                                    className="background-img"
                                    loading="eager"
                                    decoding="async"
                                />
                            </div>
                        )}

                        {/* Nút Thể lệ */}
                        {data?.ruleImgUrl && (
                            <button className="rules-btn" onClick={() => setShowRules(true)}>
                                Thể lệ
                            </button>
                        )}

                        {/* Nội dung chính của webview */}
                        <div 
                            className="main-content"
                            style={{
                                paddingBottom: status === 'Đang diễn ra' && data?.deepLinkJoinButton && !isJoinContest ? '12px' : '40px'
                            }}
                        >
                        <div className="content-row">
                            <div className="content-left" style={{ backgroundColor: startBoxBg, color: startTextColor }}>
                            <div className="date-info" style={{ marginRight: '14px' }}>
                                <div className="date-label">Bắt đầu</div>
                                <div className="date-value">{data?.timeStartRound1 ? new Date(data.timeStartRound1).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '--'}</div>
                            </div>
                            </div>
                            <div className="content-right" style={{ backgroundColor: endBoxBg, color: endTextColor }}>
                            <div className="date-info">
                                <div className="date-label" style={{ marginLeft: '12px' }}>Kết thúc</div>
                                <div className="date-value" style={{ marginLeft: '14px' }}>{data?.timeEndRound2 ? new Date(data.timeEndRound2).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '--'}</div>
                            </div>
                            </div>
                        </div>
                        <div className="content-status"  style={{ color: contestColor }}>
                            <div className="content-status-left">
                            <div className="status-label">{status}</div>
                            <div className="description-label">{getDescriptionText()}</div>
                            </div>
                            <div className="content-status-mid"></div>
                            <div className="content-status-right">
                            <div className="date-remain">{timeRemaining.days.toString().padStart(2, '0')}</div>
                            <div className="time-remain">
                                {timeRemaining.hours.toString().padStart(2, '0')} : {timeRemaining.minutes.toString().padStart(2, '0')} : {timeRemaining.seconds.toString().padStart(2, '0')}
                            </div>
                            </div>
                        </div>
                        {status !== 'Sắp diễn ra' && (
                            <>
                            {/* <div className="leaderboard-title">Bảng xếp hạng</div> */}
                            <div className="leaderboard-label">
                                <svg width="80%" height="39" viewBox="0 0 288 39" fill="none" xmlns="http://www.w3.org/2000/svg" style={{maxWidth: '288px'}}>
                                <path d="M288 19.5L279 9C279.833 10.5 282 13.5 282 19C282 24 280.167 27.5 279 29L288 19.5Z" fill="url(#paint0_linear_11147_18155)"/>
                                <path d="M0 19.5L9 9C8.16667 10.5 6 13.5 6 19C6 24 7.83333 27.5 9 29L0 19.5Z" fill="url(#paint1_linear_11147_18155)"/>
                                <rect x="5.5" y="0.5" width="277" height="38" rx="19" fill={contestColor} stroke="#FEDE31"/>
                                <text x="144" y="20" textAnchor="middle" dominantBaseline="middle" fontSize="18" fontFamily="Roboto" fill="#FFFFFF" fontWeight="400">
                                    Bảng xếp hạng
                                </text>
                                <defs>
                                    <linearGradient id="paint0_linear_11147_18155" x1="283.5" y1="13" x2="283.5" y2="25.5" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#E2B169"/>
                                    <stop offset="0.447115" stopColor="#FEDB35"/>
                                    <stop offset="1" stopColor="#E2B169"/>
                                    </linearGradient>
                                    <linearGradient id="paint1_linear_11147_18155" x1="4.5" y1="13" x2="4.5" y2="25.5" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#E2B169"/>
                                    <stop offset="0.447115" stopColor="#FEDB35"/>
                                    <stop offset="1" stopColor="#E2B169"/>
                                    </linearGradient>
                                </defs>
                                </svg>
                            </div>
                            <div className="leaderboard">
                                {leaderboardLoading ? (
                                    <div style={{display: 'flex', justifyContent: 'center'}}>
                                        <ReactLoading type="spinningBubbles" color={getThemeColor()} height={75} width={75} />
                                    </div>
                                ) : leaderboardData.length > 0 ? (
                                    leaderboardData.map((item, index) => (
                                        
                                        <div 
                                            key={index} 
                                            className="leaderboard-item" 
                                            onClick={() => {
                                                if (item.deeplink) {
                                                    window.location.href = item.deeplink;
                                                }
                                            }}
                                        >
                                            <div className="leaderboard-rank">{renderRank(index)}</div>
                                            <div className="leaderboard-avatar">
                                                <img 
                                                    src={avatarLoadedStates[index] ? item.avatar : DefaultAvatar} 
                                                    alt="Avatar" 
                                                    onLoad={() => handleAvatarLoad(index, item.avatar)}
                                                />
                                            </div>
                                            <div className="leaderboard-info">
                                                <div className="leaderboard-name">{item.firstText}</div>
                                                <div className="leaderboard-id">{item.secondText}</div>
                                                <div className="leaderboard-score">
                                                    {data?.markIcon && <img src={data.markIcon} alt="Score Icon" className="score-icon" />}
                                                    <span>{item.thirdText}</span>
                                                </div>
                                            </div>
                                            <div className="leaderboard-action">
                                                <img src={ChevronRight} alt="chevron right" />
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{textAlign: 'center', color: '#FFFFFF', marginBottom: '18px'}}>
                                        Chưa có dữ liệu bảng xếp hạng
                                    </div>
                                )}
                            </div>
                            </>
                        )}
                        </div>

                        {/* Deep Link Button - Sticky Bottom */}
                        {status === 'Đang diễn ra' && canJoin && (!isJoinContest || data?.leaderboardKind === 'RECORDING_CONTEST') && !data?.leaderboardKind?.includes('LIVEROOM') && (
                            <div 
                                className="deep-link-sticky"
                                style={{
                                    background: hexToRgba(data?.backgroundCode?.backgroundCodeFooter || '#000000', 0.8),
                                }}
                            >
                                <button 
                                    className="deep-link-btn"
                                    onClick={joinContest}
                                    disabled={joinContestLoading}
                                    style={{color: data?.backgroundCode?.backgroundCodeContest || '#000000'}}
                                >
                                    <span style={{lineHeight: '2.6'}}>THAM GIA NGAY</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}
                
            </div>
            <div className="webview-comment">

            </div>
        </>
    );
};

export default WebViewPage;

